/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ReactComponent as ArrowRight } from '@icons/Arrows/right.svg';
import { createContext, FC, InputHTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  Box,
  Button,
  ClickAwayListener,
  Paper,
  PaperProps,
  Stack,
  Typography,
  AccordionSummary,
  AccordionSummaryProps,
  styled,
  SxProps,
  IconButton,
} from '@mui/material';
import { border, buildDataTestId, buildDataTestIdAttribute, hexOpacity, log } from '@src/utils';
import { theme } from '@src/theme';
import {
  DiagnosesSearchValueType,
  IDiagnosesGroupItem,
  IDiagnosesItem,
  SelectedDiagnoses,
  diagnosesApiSlice,
  diagnosesGroupsApiSlice,
} from '@src/api';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { Checkbox } from '@components/Checkbox';
import { ReactComponent as TrashIcon } from '@icons/trash.svg';
import { SegmentedControl } from '@components/SegmentedControl';
import { defaultFilterValues, diagnosesSearchTypes } from './consts';
import { DiagnosesPaper } from './PaperComponents/DiagnosesPaper';
import { DiagnosesPaperGroups } from './PaperComponents/DiagnosesGroupsPaper';
import { ModalDeleteDiagnosesGroup } from './ModalDeleteDiagnosesGroup';
import { useFormContext } from 'react-hook-form';

const PAGGING_DIFFERENCE = 30;

const getBackgroundColorForChild = (index: number, parentBackgroundColor: '#F7FCFD' | '#FFFFFF') => {
  if (parentBackgroundColor === '#F7FCFD') {
    return index % 2 === 0 ? '#FFFFFF' : '#F7FCFD';
  } else {
    return index % 2 === 0 ? '#F7FCFD' : '#FFFFFF';
  }
};

type ContextProps = {
  addDiagnoses: (diagnoses: SelectedDiagnoses[]) => void;
  addDiagnosesGroups: (diagnosesGroups: IDiagnosesGroupItem[]) => void;
  diagnosesInRule: SelectedDiagnoses[];
  diagnosesGroupsInRule: IDiagnosesGroupItem[];
  onDelete: (id: string) => void;
  scaleType: DiagnosesSearchValueType;
  onDeleteDiagnosesGroups: (id: number) => void;
  selectedDiagnoses?: any;
  selectedDiagnosesGroups?: any;
  setDiagnosesInRule?: any;
  prefix: string;
};

export const PropsDiagnosesContext = createContext<ContextProps>({
  addDiagnoses: () => {},
  addDiagnosesGroups: () => {},
  diagnosesInRule: [],
  diagnosesGroupsInRule: [],
  onDelete: () => {},
  onDeleteDiagnosesGroups: () => {},
  scaleType: DiagnosesSearchValueType.ICD10,
  setDiagnosesInRule: () => {},
  prefix: '',
});

export const DiagnosisAccordionSummary = styled((props: AccordionSummaryProps) => <AccordionSummary {...props} />)(
  ({ theme: localTheme }) => ({
    flexDirection: 'row-reverse',
    padding: '10px 16px',
    cursor: 'pointer',
    alignItems: 'center',
    boxShadow: 'none',
    minHeight: '0',

    '& .MuiAccordionSummary-content': {
      margin: 0,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
    '&.Mui-expanded': {
      minHeight: '42px',
      marginTop: '-1px',
      marginLeft: '-1px',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      '& svg': {
        margin: 0,
      },
      '&.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
    },

    '&:hover': {
      backgroundColor: localTheme.palette.grey['100'],
    },
    '&:active': {
      backgroundColor: hexOpacity(localTheme.palette.secondary.main, '7%'),
    },
  }),
);

type SelectableDiagnosisProps = {
  diagnosis: IDiagnosesItem;
  withCheckbox?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  dataTestId?: string;
};

export const SelectableDiagnosis: FC<SelectableDiagnosisProps> = ({
  diagnosis,
  withCheckbox = true,
  checked,
  disabled,
  onChange,
  dataTestId,
}) => {
  // const { diagnosesInRule } = useContext(PropsDiagnosesContext);

  const { name, description } = diagnosis;

  return (
    <Stack ml="18px" direction="row" gap="18px" alignItems="start">
      {withCheckbox && (
        <Checkbox
          sx={{ padding: 0, zIndex: 10 }}
          onChange={onChange}
          checked={checked /* || diagnosesInRule?.some(di => di.id === id)*/}
          inputProps={{ ...buildDataTestIdAttribute(dataTestId, 'checkbox') } as InputHTMLAttributes<HTMLInputElement>}
        />
      )}
      <Typography
        color={disabled ? 'grey.400' : 'grey.700'}
        variant="label14Bold"
        lineHeight="18px"
        sx={{ flexShrink: 0 }}
      >
        {name}
      </Typography>
      <Typography color={disabled ? 'grey.400' : 'grey.700'} lineHeight="18px" variant="label14Medium">
        {description}
      </Typography>
    </Stack>
  );
};

export type SelectebleDiagnoses = Nullable<{
  data?: IDiagnosesItem[];
  brothers?: IDiagnosesItem[];
  generalParentIds?: string[];
  parentDiagnosis?: IDiagnosesItem;
  parentChecked?: boolean;
}>;

type DiagnosesCategoryProps = {
  diagnosis: IDiagnosesItem;
  setAllSelectableDiagnoses?: React.Dispatch<React.SetStateAction<SelectebleDiagnoses>>;
  searchDiagnosesValue: string;
  parentDiagnosis?: IDiagnosesItem;
  withCheckbox?: boolean;
  checked?: boolean;
  sx?: SxProps;
  level: number;
  onDelete?: (val: string) => void;
  setSelectedDiagnosesLocal?;
  allDiagnoses?: IDiagnosesItem[];
  brothers?: any;
  generalParentIds?: string[];
  withoutIcon?: boolean;
  additionalСonditionCheckbox?: boolean;
  backgroundColor?: '#F7FCFD' | '#FFFFFF';
  dataTestId?: string;
};

export const DiagnosesItem: FC<DiagnosesCategoryProps> = ({
  diagnosis,
  parentDiagnosis,
  searchDiagnosesValue,
  setAllSelectableDiagnoses,
  withCheckbox = true,
  checked,
  level,
  sx,
  onDelete,
  setSelectedDiagnosesLocal,
  allDiagnoses,
  brothers,
  generalParentIds,
  withoutIcon,
  additionalСonditionCheckbox = true,
  backgroundColor,
  dataTestId,
}) => {
  const { name, id, description, selectable } = diagnosis;
  const [open, setOpen] = useState(false);

  const [allChildrenChecked, setAllChildrenChecked] = useState<Nullable<boolean>>(null);

  const [allDiagnosesChildrenCategory, setAllDiagnosesChildrenCategory] = useState<IDiagnosesItem[] | undefined>();

  const [checkedForVisible, setCheckedForVisible] = useState(checked);

  const textRef = useRef<HTMLDivElement>(null);
  const [itemHeight, setItemHeight] = useState(48);

  useEffect(() => {
    if (textRef.current) {
      const textHeight = textRef.current.clientHeight;
      const lineHeight = 18;
      const numberOfLines = Math.ceil(textHeight / lineHeight);
      setItemHeight(numberOfLines > 1 ? 60 : 48);
    }
  }, [name, description]);

  useEffect(() => {
    if (allDiagnoses && allDiagnoses.length === 0) {
      setCheckedForVisible(false);
    }
  }, [allDiagnoses]);

  useEffect(() => {
    setCheckedForVisible(allDiagnoses?.some(({ id: diId }) => diId === id) || checked);
  }, [checked, allDiagnoses, id]);

  useEffect(() => {
    setAllChildrenChecked(
      (allDiagnosesChildrenCategory &&
        allDiagnosesChildrenCategory?.every(({ id: childId }) =>
          allDiagnoses?.some(({ id: selectedId }) => selectedId === childId),
        )) as boolean,
    );
  }, [allDiagnoses, allDiagnosesChildrenCategory, checkedForVisible, checked]);

  useEffect(() => {
    if (allChildrenChecked) {
      const idsChildren = allDiagnosesChildrenCategory?.map(child => child.id);
      setSelectedDiagnosesLocal &&
        setSelectedDiagnosesLocal(prev => [
          ...(prev || []).filter(({ id: prevId }) => !idsChildren?.includes(prevId as string)),
          diagnosis,
        ]);
    }
  }, [allChildrenChecked, diagnosis, allDiagnosesChildrenCategory, setSelectedDiagnosesLocal]);

  const [fetchDiagnosisChildren, { isFetching: isFetchingDiagnosisChildren }] =
    diagnosesApiSlice.useLazyGetDiagnosesByEsbIdQuery();

  const onChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (withoutIcon && setAllSelectableDiagnoses) {
      setAllSelectableDiagnoses(prev => ({ ...prev, parentChecked: false }));
    }

    const { checked: checkedEvent } = event.target;
    if (checkedEvent) {
      setCheckedForVisible && setCheckedForVisible(true);
      setSelectedDiagnosesLocal(prev => {
        return [...prev, { description, name, id, selectable }].filter(
          ({ id: prevId }) =>
            !allDiagnosesChildrenCategory?.some(({ id: idChildren }) => {
              return idChildren === prevId;
            }),
        );
      });
    } else {
      if (parentDiagnosis && checked && additionalСonditionCheckbox) {
        setSelectedDiagnosesLocal(prev => {
          return [
            ...(brothers || []).filter(
              ({ id: broId }: { id: string }) =>
                broId !== id && !generalParentIds?.includes(broId) && broId !== parentDiagnosis.id,
            ),
            ...prev.filter((di: { id: string }) => !generalParentIds?.includes(di.id) && di.id !== parentDiagnosis.id),
          ];
        });
      } else {
        setCheckedForVisible && setCheckedForVisible(false);
        setSelectedDiagnosesLocal(prev => prev.filter(di => di.id !== id));
      }
    }
  };

  const onChangeAccordion = () => {
    if (!open) {
      fetchDiagnosisChildren({
        search: searchDiagnosesValue,
        parentDiagnosisEsbId: id,
      })
        .then(res =>
          res.data?.data.every(dia => dia.selectable) && withCheckbox
            ? setAllSelectableDiagnoses &&
              setAllSelectableDiagnoses({
                data: res.data?.data,
                brothers: [...(brothers || []), ...res.data?.data],
                generalParentIds,
                parentDiagnosis: diagnosis,
                parentChecked: checkedForVisible || checked,
              })
            : setAllDiagnosesChildrenCategory(res.data?.data),
        )
        .catch(log);
    }
    setOpen(prev => !prev);
  };

  return (
    <Accordion
      key={`${name}/${id}`}
      sx={{
        boxShadow: 'none',
        flexGrow: 1,
        marginTop: '-1px',
        marginLeft: '-1px',
        '&:before': {
          backgroundColor: '#fff',
        },
        '&.MuiPaper-root': {
          boxShadow: 'none',
        },
        '&.Mui-expanded': {
          margin: 0,
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.grey['100'],
        },
        ...sx,
      }}
      expanded={open}
    >
      <DiagnosisAccordionSummary
        sx={{
          paddingLeft: `${12 + level * PAGGING_DIFFERENCE}px`,
          border: withCheckbox ? 'none' : border,
          justifyContent: 'space-between',
          height: `${itemHeight}px`,
        }}
        expandIcon={
          withoutIcon || selectable ? null : (
            <Button
              sx={{ minWidth: 0, padding: 0, height: 'auto' }}
              onClick={onChangeAccordion}
              {...buildDataTestIdAttribute(dataTestId, 'button-expand')}
            >
              <ArrowRight />
            </Button>
          )
        }
        {...buildDataTestIdAttribute(dataTestId)}
      >
        <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
          <Box ref={textRef}>
            <SelectableDiagnosis
              checked={checkedForVisible}
              diagnosis={diagnosis}
              withCheckbox={withCheckbox}
              onChange={onChangeCheckBox}
              dataTestId={dataTestId}
            />
          </Box>
          {onDelete && (
            <IconButton
              sx={{ padding: '1px' }}
              onClick={() => onDelete(id)}
              {...buildDataTestIdAttribute(dataTestId, 'button-delete')}
            >
              <TrashIcon />
            </IconButton>
          )}
        </Stack>
      </DiagnosisAccordionSummary>
      <AccordionDetails sx={{ padding: 0 /* , paddingLeft: '12px' */ }}>
        {isFetchingDiagnosisChildren ? (
          <PreloaderBubbles size={6} in overlay="light" />
        ) : (
          allDiagnosesChildrenCategory?.map((childrenDiagnosis, index) => {
            const backgroundColorForChild: '#F7FCFD' | '#FFFFFF' = getBackgroundColorForChild(
              index,
              backgroundColor || '#F7FCFD',
            );
            return (
              <DiagnosesItem
                sx={{ backgroundColor: backgroundColorForChild }}
                backgroundColor={backgroundColorForChild}
                parentDiagnosis={diagnosis}
                key={childrenDiagnosis.id}
                withCheckbox={withCheckbox}
                checked={checkedForVisible || checked}
                setAllSelectableDiagnoses={setAllSelectableDiagnoses}
                diagnosis={childrenDiagnosis}
                level={level + 1}
                searchDiagnosesValue={searchDiagnosesValue}
                setSelectedDiagnosesLocal={setSelectedDiagnosesLocal}
                brothers={[...(brothers || []), ...(checkedForVisible ? allDiagnosesChildrenCategory : [])]}
                allDiagnoses={allDiagnoses}
                generalParentIds={[...(generalParentIds || []), childrenDiagnosis.id]}
                dataTestId={buildDataTestId(dataTestId, `children-${index}`)}
              />
            );
          })
        )}
      </AccordionDetails>
    </Accordion>
  );
};

type DiagnosesGroupProps = {
  id: number;
  name: string;
  withDelete?: boolean;
  onDeleteDiagnosesGroupsFromRule?: (id: number) => void;
  withCheckbox?: boolean;
  sx?: SxProps;
  setSelectedDiagnosesGroupLocal?;
  selectedDiagnoses?;
  backgroundColor?: '#F7FCFD' | '#FFFFFF';
  withDeleteItem?: boolean;
  dataTestId?: string;
};

export const DiagnosesGroupItem: FC<DiagnosesGroupProps> = ({
  id,
  name,
  withDelete = true,
  withCheckbox = false,
  onDeleteDiagnosesGroupsFromRule,
  setSelectedDiagnosesGroupLocal,
  selectedDiagnoses,
  withDeleteItem = false,
  sx,
  backgroundColor,
  dataTestId,
}) => {
  const { setValue, getValues } = useFormContext();

  const { prefix } = useContext(PropsDiagnosesContext);
  const [open, setOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [checkedForVisible, setCheckedForVisible] = useState(false);

  const [fetchDiagnosesGroup, { isFetching: isFetchingDiagnosesGroupChildren, data: diagnosesGroupInfo }] =
    diagnosesGroupsApiSlice.useLazyGetDiagnosesGroupByIdQuery();
  const [deleteDiagnosesGroup] = diagnosesGroupsApiSlice.useDeleteDiagnosesGroupMutation();

  useEffect(() => {
    if (selectedDiagnoses && selectedDiagnoses.length === 0) {
      setCheckedForVisible(false);
    }
  }, [selectedDiagnoses]);

  const onChangeAccordion = () => {
    if (!open) {
      fetchDiagnosesGroup(id).catch(log);
    }
    setOpen(prev => !prev);
  };
  const onDeleteDiagnosesGroup = () => {
    withDeleteItem ? setOpenDeleteModal(true) : onDeleteDiagnosesGroupsFromRule && onDeleteDiagnosesGroupsFromRule(id);
  };
  const deleteDiagnosesGroupFromData = () => {
    deleteDiagnosesGroup(id)
      .then((res: any) => {
        if (res.data.status === 200) {
          onDeleteDiagnosesGroupsFromRule && onDeleteDiagnosesGroupsFromRule(id);

          const diagnosesGroups = getValues(`${prefix}.diagnosesGroups`);

          const diagnosisGroupIds = getValues(`${prefix}.diagnosisGroupIds`);

          setValue(
            `${prefix}.diagnosesGroups`,
            diagnosesGroups.filter(({ id: diId }) => diId !== id),
          );
          return setValue(
            `${prefix}.diagnosisGroupIds`,
            diagnosisGroupIds.filter(diId => diId !== id),
          );
        }
        return null;
      })
      .catch(log);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked: checkedEvent } = event.target;
    if (checkedEvent) {
      setCheckedForVisible(true);
      setSelectedDiagnosesGroupLocal(prev => [...prev, { id, name }]);
    } else {
      setCheckedForVisible(false);
      setSelectedDiagnosesGroupLocal(prev => prev.filter(({ id: prevId }) => prevId !== id));
    }
  };

  return (
    <>
      <Accordion
        key={`${name}/${id}`}
        sx={{
          boxShadow: 'none',
          flexGrow: 1,
          marginTop: '-1px',
          marginLeft: '-1px',
          '&:before': {
            backgroundColor: '#fff',
          },
          '&.MuiPaper-root': {
            boxShadow: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.grey['100'],
          },
          ...sx,
        }}
        expanded={open}
      >
        <DiagnosisAccordionSummary
          sx={{
            paddingLeft: '12px',
            backgroundColor: 'transparent',
            border: withCheckbox ? 'none' : border,
            justifyContent: 'space-between',
          }}
          expandIcon={
            <Button
              onClick={onChangeAccordion}
              sx={{ padding: 0, minWidth: 0, height: 'auto' }}
              {...buildDataTestIdAttribute(dataTestId, 'button-expand')}
            >
              <ArrowRight />
            </Button>
          }
          {...buildDataTestIdAttribute(dataTestId)}
        >
          <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
            <Stack ml="18px" direction="row" gap="18px" alignItems="start">
              {withCheckbox && (
                <Checkbox
                  sx={{ padding: 0, zIndex: 10 }}
                  onChange={handleChange}
                  checked={checkedForVisible || selectedDiagnoses?.some(group => group.id === id)}
                  inputProps={
                    {
                      ...buildDataTestIdAttribute(dataTestId, 'checkbox-selection'),
                    } as InputHTMLAttributes<HTMLInputElement>
                  }
                />
              )}
              <Typography color="grey.700" lineHeight="18px" variant="label14Medium">
                {name}
              </Typography>
            </Stack>
            {withDelete && (
              <IconButton
                sx={{ padding: '1px' }}
                onClick={onDeleteDiagnosesGroup}
                {...buildDataTestIdAttribute(dataTestId, 'button-delete')}
              >
                <TrashIcon />
              </IconButton>
            )}
          </Stack>
        </DiagnosisAccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {isFetchingDiagnosesGroupChildren ? (
            <PreloaderBubbles size={6} in overlay="light" />
          ) : (
            diagnosesGroupInfo?.diagnoses?.map((diagnosis, index) => {
              const backgroundColorForChild: '#F7FCFD' | '#FFFFFF' = getBackgroundColorForChild(
                index,
                backgroundColor || '#F7FCFD',
              );
              return (
                <DiagnosesItem
                  sx={{
                    '&.Mui-expanded': {
                      // border,
                      padding: 0,
                      margin: 0,
                    },
                    backgroundColor: backgroundColorForChild,
                  }}
                  level={1}
                  withCheckbox={false}
                  key={diagnosis.id}
                  diagnosis={{
                    ...diagnosis,
                    description: diagnosis.description,
                    id: diagnosis.id,
                    name: diagnosis.name,
                    selectable: false,
                  }}
                  searchDiagnosesValue={''}
                  backgroundColor={backgroundColorForChild}
                  dataTestId={buildDataTestId(dataTestId, `giagnosis-in-group-${index}`)}
                />
              );
            })
          )}
        </AccordionDetails>
      </Accordion>
      <ModalDeleteDiagnosesGroup
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        deleteDiagnosesGroup={deleteDiagnosesGroupFromData}
      />
    </>
  );
};

export const PaperComponent = ({
  children,
  search,
  handleCancel,
  handleClickScaleType,
  isGroupsPage = false,
  dataTestId,
  ...paperProps
}: PaperProps & {
  handleCancel: () => void;
  search: string;
  handleClickScaleType?: (val: DiagnosesSearchValueType) => void;
  isGroupsPage?: boolean;
  dataTestId?: string;
}) => {
  const { scaleType, addDiagnoses, addDiagnosesGroups, diagnosesInRule, diagnosesGroupsInRule } =
    useContext(PropsDiagnosesContext);

  const [typeVisible, setTypeVisible] = useState(DiagnosesSearchValueType.ICD10);

  const [selectedDiagnosesLocal, setSelectedDiagnosesLocal] = useState(diagnosesInRule || []);

  const [selectedDiagnosesGroupsLocal, setSelectedDiagnosesGroupLocal] = useState(diagnosesGroupsInRule || []);

  const { data: diagnosesGroups } = diagnosesGroupsApiSlice.useGetDiagnosesGroupsQuery(defaultFilterValues);

  useEffect(() => {
    if (diagnosesGroupsInRule?.length > 0) setSelectedDiagnosesGroupLocal(diagnosesGroupsInRule);
  }, [diagnosesGroupsInRule]);

  useEffect(() => {
    if (diagnosesInRule) setSelectedDiagnosesLocal(diagnosesInRule);
  }, [diagnosesInRule]);

  const onChangeVisible = (val: DiagnosesSearchValueType) => {
    setTypeVisible(val);
  };

  const handleApply = () => {
    addDiagnoses(selectedDiagnosesLocal || []);
    addDiagnosesGroups && addDiagnosesGroups(selectedDiagnosesGroupsLocal || []);
    handleCancel();
  };

  const handleClearAll = () => {
    setSelectedDiagnosesGroupLocal([]);
    setSelectedDiagnosesLocal([]);
  };

  return (
    <Box
      height={scaleType && !isGroupsPage ? 'calc(100% - 48px)' : '100%'}
      sx={{ '& .MuiPaper-root': { overflow: 'hidden' } }}
    >
      <ClickAwayListener onClickAway={handleCancel}>
        <Paper
          sx={{
            boxShadow: '0px 8px 12px rgba(55, 65, 81, 0.1), 0px 0px 6px rgba(55, 65, 81, 0.1)',
            height: '100%',
            zIndex: 1,
          }}
          {...paperProps}
        >
          {typeVisible && handleClickScaleType ? (
            <SegmentedControl
              active={typeVisible}
              values={diagnosesGroups && diagnosesGroups?.length > 0 ? diagnosesSearchTypes : [diagnosesSearchTypes[0]]}
              handleClick={onChangeVisible}
              sx={{
                height: '48px',
                alignItems: 'center',
                width: '100%',
                backgroundColor: 'white',
                borderBottom: border,
                zIndex: 100,
                position: 'relative',
                button: {
                  height: '24px',
                  padding: '4px 12px',
                  '&:first-of-type': {
                    marginLeft: '16px',
                  },
                },
              }}
              withTypography
              prefix={'modal-add-diagnoses'}
            />
          ) : null}
          {typeVisible === DiagnosesSearchValueType.ICD10 ? (
            <DiagnosesPaper
              search={search}
              allDiagnoses={selectedDiagnosesLocal}
              setSelectedDiagnosesLocal={setSelectedDiagnosesLocal}
              dataTestId={dataTestId}
            />
          ) : (
            <DiagnosesPaperGroups
              search={search}
              allDiagnosesGroups={selectedDiagnosesGroupsLocal}
              setSelectedDiagnosesGroupLocal={setSelectedDiagnosesGroupLocal}
              dataTestId={dataTestId}
            />
          )}

          <Stack
            padding="12px"
            direction="row"
            justifyContent="space-between"
            position="absolute"
            bottom="0"
            width="100%"
            borderTop={`1px solid ${hexOpacity(theme.palette.secondary.main, '14%')}`}
            zIndex={20}
            bgcolor="#FFFFFF"
            boxSizing="border-box"
          >
            <Button
              disabled={false}
              color="secondary"
              size="small"
              variant="outlined"
              sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
              onClick={handleCancel}
              {...buildDataTestIdAttribute(dataTestId, 'button-cancel')}
            >
              Cancel
            </Button>
            <Stack gap="12px" direction="row">
              <Button
                disabled={false}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
                onClick={handleClearAll}
                {...buildDataTestIdAttribute(dataTestId, 'button-clear-all')}
              >
                Clear all
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={selectedDiagnosesLocal?.length === 0 && selectedDiagnosesGroupsLocal?.length === 0}
                size="small"
                sx={{ width: '78px', minWidth: '78px', padding: 0, height: '30px', fontWeight: 500, fontSize: '14px' }}
                onClick={handleApply}
                {...buildDataTestIdAttribute(dataTestId, 'button-apply')}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Box>
  );
};
